import { debugActions } from '@mkp/debug/actions';
import {
  FeatureFlag,
  FeatureFlags,
  getFeatureFlagsInitialState,
  getShouldSyncDebug,
} from '@mkp/debug/models';
import { BaseEnvironment } from '@mkp/shared/models';
import { objectKeys } from '@mkp/shared/util-format';
import { Action, ActionReducer, createFeature, createReducer, MetaReducer, on } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';

export interface DebugState {
  defaultFlags: FeatureFlags;
  featureFlagOverrides: Partial<FeatureFlags>;
  editMode: boolean;
  isLocked: boolean;
}

export const getInitialState = (environment?: BaseEnvironment): DebugState => ({
  defaultFlags: getFeatureFlagsInitialState(environment),
  featureFlagOverrides: {},
  editMode: false,
  isLocked: false,
});

const setOverride = (
  { defaultFlags, featureFlagOverrides }: DebugState,
  featureFlag: FeatureFlag,
  value: boolean
): Partial<FeatureFlags> => {
  const { [featureFlag]: _dropped, ...withoutFlag } = featureFlagOverrides;

  return defaultFlags[featureFlag] === value
    ? withoutFlag
    : { ...featureFlagOverrides, [featureFlag]: value };
};

const reducer = createReducer(
  getInitialState(),
  on(debugActions.toggleEditableContent, ({ editMode, ...state }) => ({
    ...state,
    editMode: !editMode,
  })),
  on(debugActions.toggleFeatureFlag, (state, { featureFlag, value }) =>
    state.isLocked
      ? state
      : { ...state, featureFlagOverrides: setOverride(state, featureFlag, value) }
  ),
  on(debugActions.setEnvironmentDefaults, (state, { defaultFlags, isLocked }) => ({
    ...state,
    defaultFlags,
    isLocked,
  }))
);

export const debugFeature = createFeature({ name: 'debugState', reducer });

const getKeysToSync = (): FeatureFlag[] =>
  objectKeys(getFeatureFlagsInitialState()).filter((key) => key !== 'shouldTriggerSilentUpdate');

export const createDebugSyncReducer =
  (environment: BaseEnvironment): MetaReducer =>
  (reducer: ActionReducer<DebugState, Action>) =>
    localStorageSync({
      keys: [{ [debugFeature.name]: [{ featureFlagOverrides: getKeysToSync() }] }],
      storageKeySerializer: (key) => `jobcloud.${key}`,
      rehydrate: true,
      syncCondition: () => getShouldSyncDebug(environment),
    })(reducer);
